import React, { useContext, useEffect, useMemo, useRef, useState } from 'react'
import ky from '../Modules/ApiClient'
import toast from 'react-hot-toast'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faArrowRightFromBracket, faChartLine, faClipboardCheck, faHouse, faMagnifyingGlass, faPen, faPlus, faShieldHalved, faSpinner, faTriangleExclamation, faUser, faUserGear } from '@fortawesome/free-solid-svg-icons'
import { Chart as MixedChart } from 'react-chartjs-2'
import { BarController, BarElement, CategoryScale, Chart as ChartJS, Legend, LinearScale, LineController, LineElement, PointElement, Tooltip } from 'chart.js'
import { GlobalContext } from '../App'
import { API_URL } from '../Globals'
import { AppPermissionId, manageablePermissionGroups, manageablePermissions, togglePermission } from '../Permissions/PermissionDefinitions'
import { PASSWORD_MAX_LENGTH, PASSWORD_MIN_LENGTH, passwordStrengthLabel, validateNewPassword } from './PasswordPolicy'
import LoginHistory from './LoginHistory'
import ActivityErrorBoundary from './ActivityErrorBoundary'
import { endSession } from '../Modules/Session'

ChartJS.register(BarController, LineController, CategoryScale, LinearScale, BarElement, LineElement, PointElement, Tooltip, Legend)

interface ManagedUser {
  _id: string
  username: string
  firstName: string
  lastName: string
  permissions: string[]
  disabled?: boolean
  online?: boolean
  lastLoginAt?: string
  lastActivityAt?: string
}

interface NewUserDraft {
  username: string
  firstName: string
  lastName: string
  permissions: string[]
}

interface UserActivity {
  periodStart: string
  periodEnd: string
  totals: { modifiedAddresses: number, createdAddresses: number, taskEntries: number }
  months: { key: string, addressEvents: number, taskEvents: number }[]
  recentEvents: { id: string, action: string, entityType: string, summary: string, createdAt: string }[]
}

const formatAccountTime = (value?: string): string => {
  if (!value) return 'Ei tietoa'
  const date = new Date(value)
  return Number.isNaN(date.getTime()) ? 'Ei tietoa' :
    date.toLocaleString('fi-FI', { dateStyle: 'short', timeStyle: 'short' })
}

const userPresenceLabel = (user: ManagedUser): string => (
  user.disabled ? 'Pois käytöstä' :
  user.online ? 'Paikalla' :
  user.lastActivityAt ? 'Poissa' : 'Ei tietoa'
)

const userPresenceStyle = (user: ManagedUser): string => (
  user.disabled ? 'bg-gray-200 text-gray-600' :
  user.online ? 'bg-emerald-100 text-emerald-700' :
  'bg-slate-100 text-slate-600'
)

const hasValidActivityShape = (value: unknown): value is UserActivity => {
  if (!value || typeof value !== 'object') return false
  const result = value as Partial<UserActivity>
  return Boolean(result.totals && Number.isFinite(result.totals.modifiedAddresses) &&
    Number.isFinite(result.totals.createdAddresses) && Number.isFinite(result.totals.taskEntries) &&
    Array.isArray(result.months) && result.months.every(month => (
      typeof month.key === 'string' &&
      Number.isFinite(month.addressEvents) && Number.isFinite(month.taskEvents)
    )) && Array.isArray(result.recentEvents))
}

const emptyNewUser = (): NewUserDraft => ({
  username: '',
  firstName: '',
  lastName: '',
  permissions: []
})

const PermissionEditor: React.FC<{ permissions: string[], onToggle: (permission: AppPermissionId) => void }> = ({ permissions, onToggle }) => (
  <fieldset className="rounded-xl border border-gray-200 p-4 sm:col-span-2">
    <legend className="px-1 text-sm font-bold text-gray-700">Käyttöoikeudet</legend>
    <p className="mb-4 text-sm text-gray-500">Valitse vain ne toiminnot, joita käyttäjä tarvitsee. Tarvittavat katseluoikeudet valitaan automaattisesti.</p>
    <div className="space-y-5">
      {manageablePermissionGroups.map(group => (
        <section key={group.title} aria-labelledby={`permission-group-${group.title}`}>
          <h3 id={`permission-group-${group.title}`} className="font-bold text-gray-800">{group.title}</h3>
          <p className="mb-2 text-sm text-gray-500">{group.description}</p>
          <div className="grid gap-3 sm:grid-cols-2">
            {group.permissions.map(permission => (
              <label key={permission.id} className={`flex min-h-20 cursor-pointer items-start gap-3 rounded-xl border p-3 transition ${permissions.includes(permission.id) ? 'border-blue-300 bg-blue-50' : 'border-gray-200 bg-white hover:bg-gray-50'}`}>
                <input type="checkbox" checked={permissions.includes(permission.id)} onChange={() => onToggle(permission.id)} className="mt-0.5 h-5 w-5 flex-none rounded border-gray-300 text-blue-600" />
                <span>
                  <span className="block font-semibold text-gray-800">{permission.label}</span>
                  <span className="mt-0.5 block text-sm font-normal leading-snug text-gray-500">{permission.description}</span>
                </span>
              </label>
            ))}
          </div>
        </section>
      ))}
    </div>
  </fieldset>
)

const UserAccounts: React.FC = () => {
  const { globalVariable } = useContext(GlobalContext)
  const [users, setUsers] = useState<ManagedUser[]>([])
  const [selected, setSelected] = useState<ManagedUser | null>(null)
  const [password, setPassword] = useState('')
  const [passwordConfirmation, setPasswordConfirmation] = useState('')
  const [isCreating, setIsCreating] = useState(false)
  const [newUser, setNewUser] = useState<NewUserDraft>(emptyNewUser)
  const [newPassword, setNewPassword] = useState('')
  const [newPasswordConfirmation, setNewPasswordConfirmation] = useState('')
  const [creating, setCreating] = useState(false)
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState('')
  const [focusedUserId, setFocusedUserId] = useState<string | null>(null)
  const [detailTab, setDetailTab] = useState<'permissions' | 'activity' | 'logins'>('permissions')
  const [search, setSearch] = useState('')
  const [activity, setActivity] = useState<UserActivity | null>(null)
  const [activityLoading, setActivityLoading] = useState(false)
  const [activityError, setActivityError] = useState('')
  const [sessionResetConfirm, setSessionResetConfirm] = useState(false)
  const [resettingSessions, setResettingSessions] = useState(false)
  const detailRef = useRef<HTMLElement>(null)

  const headers = {
    Authorization: `Bearer ${globalVariable.sessionToken || ''}`,
    Token: localStorage.getItem('token') || ''
  }

  useEffect(() => {
    let active = true
    const refreshUsers = (initial: boolean) => {
      ky.get(`${API_URL}/api/user/all`, {
        headers: {
          Authorization: `Bearer ${globalVariable.sessionToken || ''}`,
          Token: localStorage.getItem('token') || ''
        }
      }).json<ManagedUser[]>()
        .then(data => {
          if (!active) return
          setUsers(data)
          setError('')
          setFocusedUserId(current => current && data.some(user => user._id === current)
            ? current : data[0]?._id || null)
        })
        .catch(() => {
          if (active && initial) setError('Käyttäjätilejä ei voitu ladata.')
        })
        .finally(() => {
          if (active && initial) setLoading(false)
        })
    }

    refreshUsers(true)
    const refreshTimer = window.setInterval(() => {
      if (document.visibilityState === 'visible') refreshUsers(false)
    }, 30_000)
    return () => {
      active = false
      window.clearInterval(refreshTimer)
    }
  }, [globalVariable.sessionToken])

  const focusedUser = users.find(user => user._id === focusedUserId) || null

  useEffect(() => {
    if (detailTab !== 'activity' || !focusedUserId) return
    let active = true
    setActivityLoading(true)
    setActivityError('')
    setActivity(null)
    ky.get(`${API_URL}/api/user/${focusedUserId}/activity`, { headers }).json<UserActivity>()
      .then(data => {
        if (!active) return
        if (hasValidActivityShape(data)) {
          setActivity(data)
        } else {
          setActivity(null)
          setActivityError('Aktiivisuustiedot ovat puutteellisia. Kokeile myöhemmin uudelleen.')
        }
      })
      .catch(() => { if (active) setActivityError('Käyttäjän tapahtumatietoja ei voitu ladata.') })
      .finally(() => { if (active) setActivityLoading(false) })
    return () => { active = false }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [detailTab, focusedUserId])

  const visibleUsers = useMemo(() => {
    const query = search.trim().toLocaleLowerCase('fi-FI')
    if (!query) return users
    return users.filter(user => `${user.firstName} ${user.lastName} ${user.username}`.toLocaleLowerCase('fi-FI').includes(query))
  }, [search, users])

  const changePermission = (permission: AppPermissionId) => {
    if (!selected) return
    setSelected({
      ...selected,
      permissions: togglePermission(selected.permissions, permission)
    })
  }

  const changeNewUserPermission = (permission: AppPermissionId) => {
    setNewUser(current => ({
      ...current,
      permissions: togglePermission(current.permissions, permission)
    }))
  }

  const closeCreateForm = () => {
    setIsCreating(false)
    setNewUser(emptyNewUser())
    setNewPassword('')
    setNewPasswordConfirmation('')
  }

  const createUser = async () => {
    const username = newUser.username.trim()
    const firstName = newUser.firstName.trim()
    const lastName = newUser.lastName.trim()
    if (!username || !firstName || !lastName) {
      toast.error('Täytä käyttäjänimi, etunimi ja sukunimi.')
      return
    }

    const validationError = !newPassword && !newPasswordConfirmation
      ? 'Salasana vaaditaan.'
      : validateNewPassword(newPassword, newPasswordConfirmation)
    if (validationError) {
      toast.error(validationError)
      return
    }

    setCreating(true)
    try {
      const created = await ky.post(`${API_URL}/api/user`, {
        headers,
        json: {
          username,
          firstName,
          lastName,
          password: newPassword,
          permissions: newUser.permissions
        }
      }).json<ManagedUser>()
      setUsers(current => [...current, created].sort((left, right) => (
        `${left.firstName} ${left.lastName}`.localeCompare(`${right.firstName} ${right.lastName}`, 'fi')
      )))
      setFocusedUserId(created._id)
      closeCreateForm()
      toast.success(`Käyttäjä ${created.username} luotiin`)
    } catch (requestError: any) {
      const responseBody = await requestError?.response?.json?.().catch(() => null)
      toast.error(typeof responseBody?.error === 'string' ? responseBody.error : 'Käyttäjätilin luominen epäonnistui.')
    } finally {
      setCreating(false)
    }
  }

  const saveUser = async () => {
    if (!selected) return
    const passwordError = validateNewPassword(password, passwordConfirmation)
    if (passwordError) {
      toast.error(passwordError)
      return
    }

    setSaving(true)
    try {
      const updated = await ky.post(`${API_URL}/api/user/update/${selected._id}`, {
        headers,
        json: {
          username: selected.username,
          firstName: selected.firstName,
          lastName: selected.lastName,
          permissions: selected.permissions,
          disabled: Boolean(selected.disabled),
          ...(password ? { password } : {})
        }
      }).json<ManagedUser>()
      setUsers(current => current.map(user => user._id === updated._id ? updated : user))
      setSelected(updated)
      setPassword('')
      setPasswordConfirmation('')
      if (password && selected.username === globalVariable.username) {
        toast.success('Salasana vaihdettu. Kirjaudu uudelleen.')
        window.setTimeout(() => {
          endSession()
        }, 900)
      } else {
        toast.success(password ? 'Käyttäjätili ja salasana päivitetty' : 'Käyttäjätili päivitetty')
      }
    } catch (requestError: any) {
      const responseBody = await requestError?.response?.json?.().catch(() => null)
      const message = typeof responseBody?.error === 'string'
        ? responseBody.error
        : 'Käyttäjätilin tallennus epäonnistui.'
      toast.error(message)
    } finally {
      setSaving(false)
    }
  }

  const focusUser = (userId: string) => {
    setFocusedUserId(userId)
    setDetailTab('permissions')
    if (window.innerWidth < 1024) window.setTimeout(() => detailRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' }), 0)
  }

  const manageUser = (user: ManagedUser) => {
    closeCreateForm()
    setSelected({ ...user, permissions: [...(user.permissions || [])] })
    setPassword('')
    setPasswordConfirmation('')
    setSessionResetConfirm(false)
    window.scrollTo({ top: 0, behavior: 'smooth' })
  }

  const resetSessions = async () => {
    if (!selected) return
    setResettingSessions(true)
    try {
      await ky.post(`${API_URL}/api/user/${selected._id}/reset-sessions`, { headers })
      toast.success(`Käyttäjän ${selected.username} istunnot katkaistiin`)
      setSessionResetConfirm(false)
      if (selected.username === globalVariable.username) {
        endSession()
      }
    } catch (requestError: any) {
      const responseBody = await requestError?.response?.json?.().catch(() => null)
      toast.error(typeof responseBody?.error === 'string' ? responseBody.error : 'Istuntojen katkaiseminen epäonnistui.')
    } finally {
      setResettingSessions(false)
    }
  }

  const initials = (user: ManagedUser) => (
    `${(user.firstName || '').charAt(0)}${(user.lastName || '').charAt(0)}`.toLocaleUpperCase('fi-FI') ||
    (user.username || '').slice(0, 2).toLocaleUpperCase('fi-FI')
  )

  const selectedPermissionDefinitions = focusedUser
    ? manageablePermissions.filter(permission => focusedUser.permissions?.includes(permission.id))
    : []
  const activityChartData = activity ? {
    labels: activity.months.map(month => new Date(`${month.key}-01T12:00:00`).toLocaleDateString('fi-FI', { month: 'short' })),
    datasets: [
      {
        type: 'bar' as const,
        label: 'Kohdemuutokset',
        data: activity.months.map(month => month.addressEvents),
        backgroundColor: '#2563eb',
        borderRadius: 5,
        barPercentage: 0.55
      },
      {
        type: 'line' as const,
        label: 'Työmerkinnät',
        data: activity.months.map(month => month.taskEvents),
        borderColor: '#059669',
        backgroundColor: '#059669',
        pointBackgroundColor: '#059669',
        pointRadius: 3,
        borderWidth: 2,
        tension: 0.3
      }
    ]
  } : null
  const activityChartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: { position: 'bottom' as const, align: 'start' as const, labels: { boxWidth: 11, boxHeight: 11, usePointStyle: true } },
      tooltip: { mode: 'index' as const, intersect: false }
    },
    scales: {
      x: { grid: { display: false }, border: { display: false } },
      y: { beginAtZero: true, ticks: { precision: 0 }, border: { display: false }, grid: { color: '#e5e7eb' } }
    }
  }
  const chartDescription = activity
    ? activity.months.map(month => `${month.key}: ${month.addressEvents} kohdemuutosta ja ${month.taskEvents} työmerkintää`).join('. ')
    : ''

  const passwordError = validateNewPassword(password, passwordConfirmation)
  const passwordStrength = passwordStrengthLabel(password)
  const newUserPasswordError = !newPassword && !newPasswordConfirmation ? 'Salasana vaaditaan.' : validateNewPassword(newPassword, newPasswordConfirmation)
  const newUserPasswordStrength = passwordStrengthLabel(newPassword)

  return (
    <main className="mobile-page container mx-auto mb-10 px-3 sm:px-6 lg:px-8">
      <header className="mobile-page-heading mb-5 rounded-2xl border border-gray-200 bg-white p-4 shadow-sm sm:p-6">
        <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
          <div className="flex items-start gap-3">
            <div className="flex h-11 w-11 flex-none items-center justify-center rounded-xl bg-blue-50 text-blue-600"><FontAwesomeIcon icon={faUserGear} /></div>
            <div>
              <h1 className="text-2xl font-bold text-gray-900">Käyttäjätilit</h1>
              <p className="mt-1 text-sm text-gray-500">Luo ja muokkaa käyttäjätilejä tai ota tili pois käytöstä. Tilejä ei poisteta.</p>
            </div>
          </div>
          <button
            type="button"
            onClick={() => { setSelected(null); setIsCreating(true) }}
            disabled={isCreating}
            className="flex min-h-11 items-center justify-center gap-2 rounded-xl bg-blue-600 px-4 font-semibold text-white shadow-sm transition hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 disabled:cursor-default disabled:opacity-60"
          >
            <FontAwesomeIcon icon={faPlus} />
            Uusi käyttäjä
          </button>
        </div>
      </header>

      {isCreating && (
        <section className="mobile-panel mb-5 rounded-2xl border border-blue-200 bg-white shadow-sm" aria-labelledby="create-user-title">
          <div className="border-b border-gray-200 bg-blue-50 px-4 py-4 sm:px-6">
            <h2 id="create-user-title" className="text-xl font-bold text-blue-800">Luo uusi käyttäjätili</h2>
            <p className="mt-1 text-sm text-blue-700/75">Uusi tili on heti käytössä tallentamisen jälkeen.</p>
          </div>
          <div className="grid grid-cols-1 gap-4 p-4 sm:grid-cols-2 sm:p-6">
            <label className="text-sm font-semibold text-gray-700 sm:col-span-2">Käyttäjänimi<input value={newUser.username} maxLength={100} onChange={event => setNewUser(current => ({ ...current, username: event.target.value }))} autoComplete="off" className="account-field" required /></label>
            <label className="text-sm font-semibold text-gray-700">Etunimi<input value={newUser.firstName} maxLength={100} onChange={event => setNewUser(current => ({ ...current, firstName: event.target.value }))} autoComplete="off" className="account-field" required /></label>
            <label className="text-sm font-semibold text-gray-700">Sukunimi<input value={newUser.lastName} maxLength={100} onChange={event => setNewUser(current => ({ ...current, lastName: event.target.value }))} autoComplete="off" className="account-field" required /></label>

            <section className="rounded-xl border border-gray-200 bg-gray-50 p-4 sm:col-span-2" aria-labelledby="new-user-password-title">
              <h3 id="new-user-password-title" className="font-bold text-gray-800">Aloitussalasana</h3>
              <p className="mt-1 text-sm text-gray-500">Anna salasana käyttäjälle turvallista kanavaa pitkin.</p>
              <div className="mt-3 grid gap-3 sm:grid-cols-2">
                <label className="text-sm font-semibold text-gray-700">Salasana<input type="password" value={newPassword} maxLength={PASSWORD_MAX_LENGTH} onChange={event => setNewPassword(event.target.value)} placeholder={`Vähintään ${PASSWORD_MIN_LENGTH} merkkiä`} autoComplete="new-password" className="account-field" required /></label>
                <label className="text-sm font-semibold text-gray-700">Vahvista salasana<input type="password" value={newPasswordConfirmation} maxLength={PASSWORD_MAX_LENGTH} onChange={event => setNewPasswordConfirmation(event.target.value)} placeholder="Kirjoita salasana uudelleen" autoComplete="new-password" className="account-field" required /></label>
              </div>
              <div className="mt-3 flex flex-wrap items-center justify-between gap-2 text-sm">
                <p className="text-gray-500">{PASSWORD_MIN_LENGTH}–{PASSWORD_MAX_LENGTH} merkkiä. Kaikki merkit ja välilyönnit ovat sallittuja.</p>
                {newUserPasswordStrength && <p className={`font-bold ${newUserPasswordStrength.color}`}>{newUserPasswordStrength.label}</p>}
              </div>
              {(newPassword || newPasswordConfirmation) && newUserPasswordError && <p role="alert" className="mt-2 text-sm font-semibold text-red-600">{newUserPasswordError}</p>}
            </section>

            <PermissionEditor permissions={newUser.permissions} onToggle={changeNewUserPermission} />
          </div>
          <div className="grid gap-3 border-t border-gray-200 bg-gray-50 p-4 sm:grid-cols-2 sm:px-6">
            <button type="button" onClick={createUser} disabled={creating || Boolean(newUserPasswordError)} className="min-h-12 rounded-xl bg-blue-600 px-5 font-semibold text-white hover:bg-blue-700 disabled:cursor-not-allowed disabled:opacity-60">{creating ? <><FontAwesomeIcon icon={faSpinner} className="mr-2 animate-spin" />Luodaan</> : 'Luo käyttäjä'}</button>
            <button type="button" onClick={closeCreateForm} disabled={creating} className="min-h-12 rounded-xl border border-gray-300 bg-white px-5 font-semibold text-gray-700 hover:bg-gray-100 disabled:opacity-60">Peruuta</button>
          </div>
        </section>
      )}

      {selected && (
        <section className="mobile-panel mb-5 rounded-2xl border border-blue-200 bg-white shadow-sm" aria-labelledby="edit-user-title">
          <div className="border-b border-gray-200 bg-blue-50 px-4 py-4 sm:px-6">
            <h2 id="edit-user-title" className="text-xl font-bold text-blue-800">Muokkaa käyttäjää {selected.username}</h2>
          </div>
          <div className="grid grid-cols-1 gap-4 p-4 sm:grid-cols-2 sm:p-6">
            <label className="text-sm font-semibold text-gray-700 sm:col-span-2">Käyttäjänimi<input value={selected.username} maxLength={100} onChange={event => setSelected({ ...selected, username: event.target.value })} className="account-field" /></label>
            <label className="text-sm font-semibold text-gray-700">Etunimi<input value={selected.firstName} onChange={event => setSelected({ ...selected, firstName: event.target.value })} className="account-field" /></label>
            <label className="text-sm font-semibold text-gray-700">Sukunimi<input value={selected.lastName} onChange={event => setSelected({ ...selected, lastName: event.target.value })} className="account-field" /></label>

            <section className="rounded-xl border border-gray-200 bg-gray-50 p-4 sm:col-span-2" aria-labelledby="password-change-title">
              <h3 id="password-change-title" className="font-bold text-gray-800">Vaihda salasana</h3>
              <p className="mt-1 text-sm text-gray-500">Jätä kentät tyhjiksi, jos salasanaa ei vaihdeta. Vaihto katkaisee käyttäjän aiemmat istunnot.</p>
              <div className="mt-3 grid gap-3 sm:grid-cols-2">
                <label className="text-sm font-semibold text-gray-700">Uusi salasana<input type="password" value={password} maxLength={PASSWORD_MAX_LENGTH} onChange={event => setPassword(event.target.value)} placeholder={`Vähintään ${PASSWORD_MIN_LENGTH} merkkiä`} autoComplete="new-password" className="account-field" /></label>
                <label className="text-sm font-semibold text-gray-700">Vahvista uusi salasana<input type="password" value={passwordConfirmation} maxLength={PASSWORD_MAX_LENGTH} onChange={event => setPasswordConfirmation(event.target.value)} placeholder="Kirjoita salasana uudelleen" autoComplete="new-password" className="account-field" /></label>
              </div>
              <div className="mt-3 flex flex-wrap items-center justify-between gap-2 text-sm">
                <p className="text-gray-500">{PASSWORD_MIN_LENGTH}–{PASSWORD_MAX_LENGTH} merkkiä. Kaikki merkit ja välilyönnit ovat sallittuja.</p>
                {passwordStrength && <p className={`font-bold ${passwordStrength.color}`}>{passwordStrength.label}</p>}
              </div>
              {passwordError && <p role="alert" className="mt-2 text-sm font-semibold text-red-600">{passwordError}</p>}
            </section>

            <section className="rounded-xl border border-gray-200 bg-white p-4 sm:col-span-2" aria-labelledby="session-management-title">
              <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
                <div className="flex items-start gap-3"><span className="flex h-10 w-10 flex-none items-center justify-center rounded-xl bg-amber-50 text-amber-700"><FontAwesomeIcon icon={faShieldHalved} /></span><div><h3 id="session-management-title" className="font-bold text-gray-800">Istuntojen hallinta</h3><p className="mt-0.5 text-sm text-gray-500">Katkaisee käyttäjän istunnot kaikilla laitteilla. Salasana ei muutu.</p></div></div>
                <button type="button" onClick={() => setSessionResetConfirm(true)} disabled={resettingSessions} className="flex min-h-11 flex-none items-center justify-center gap-2 rounded-xl border border-amber-300 bg-amber-50 px-4 font-semibold text-amber-800 transition hover:bg-amber-100 disabled:opacity-60"><FontAwesomeIcon icon={faArrowRightFromBracket} />Katkaise kaikki istunnot</button>
              </div>
              {sessionResetConfirm && <div role="alertdialog" aria-modal="false" aria-labelledby="session-reset-confirm-title" className="mt-4 rounded-xl border border-amber-300 bg-amber-50 p-4"><div className="flex items-start gap-3"><FontAwesomeIcon icon={faTriangleExclamation} className="mt-1 text-amber-700" /><div><h4 id="session-reset-confirm-title" className="font-bold text-amber-950">Katkaistaanko käyttäjän {selected.username} kaikki istunnot?</h4><p className="mt-1 text-sm text-amber-900">Käyttäjän täytyy kirjautua uudelleen jokaisella laitteella.{selected.username === globalVariable.username ? ' Tämä kirjaa myös sinut ulos heti.' : ''}</p></div></div><div className="mt-4 grid gap-2 sm:grid-cols-2"><button type="button" onClick={() => void resetSessions()} disabled={resettingSessions} className="min-h-11 rounded-xl bg-amber-700 px-4 font-bold text-white hover:bg-amber-800 disabled:opacity-60">{resettingSessions ? <><FontAwesomeIcon icon={faSpinner} className="mr-2 animate-spin" />Katkaistaan…</> : 'Vahvista katkaisu'}</button><button type="button" onClick={() => setSessionResetConfirm(false)} disabled={resettingSessions} className="min-h-11 rounded-xl border border-amber-300 bg-white px-4 font-bold text-amber-900 hover:bg-amber-100">Peruuta</button></div></div>}
            </section>

            <PermissionEditor permissions={selected.permissions} onToggle={changePermission} />

            <label className="flex min-h-12 items-center gap-3 rounded-xl border border-gray-200 bg-gray-50 px-4 font-semibold text-gray-700 sm:col-span-2">
              <input type="checkbox" checked={!selected.disabled} disabled={selected.username === globalVariable.username} onChange={event => setSelected({ ...selected, disabled: !event.target.checked })} className="h-5 w-5 rounded border-gray-300 text-blue-600" />
              Tili käytössä
              {selected.username === globalVariable.username && <span className="text-xs font-normal text-gray-400">Omaa tiliä ei voi poistaa käytöstä</span>}
            </label>
          </div>
          <div className="grid gap-3 border-t border-gray-200 bg-gray-50 p-4 sm:grid-cols-2 sm:px-6">
            <button onClick={saveUser} disabled={saving || Boolean(passwordError)} className="min-h-12 rounded-xl bg-blue-600 px-5 font-semibold text-white hover:bg-blue-700 disabled:cursor-not-allowed disabled:opacity-60">{saving ? <><FontAwesomeIcon icon={faSpinner} className="mr-2 animate-spin" />Tallennetaan</> : 'Tallenna'}</button>
            <button onClick={() => { setSelected(null); setPassword(''); setPasswordConfirmation(''); setSessionResetConfirm(false) }} className="min-h-12 rounded-xl border border-gray-300 bg-white px-5 font-semibold text-gray-700 hover:bg-gray-100">Sulje</button>
          </div>
        </section>
      )}

      {loading ? (
        <div className="flex justify-center py-12 text-blue-600"><FontAwesomeIcon icon={faSpinner} className="mr-2 animate-spin" />Ladataan käyttäjiä…</div>
      ) : error ? (
        <div role="alert" className="rounded-2xl border border-red-200 bg-red-50 p-5 text-center font-medium text-red-700">{error}</div>
      ) : (
        <section className="mobile-panel overflow-hidden rounded-2xl border border-gray-200 bg-white shadow-sm" aria-label="Käyttäjätilien hallinta">
          <div className="grid min-h-[38rem] lg:grid-cols-[22rem_minmax(0,1fr)]">
            <aside className="border-b border-gray-200 lg:border-b-0 lg:border-r" aria-label="Käyttäjälista">
              <div className="border-b border-gray-200 p-4">
                <label className="relative block">
                  <span className="sr-only">Hae käyttäjää</span>
                  <FontAwesomeIcon icon={faMagnifyingGlass} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-gray-400" />
                  <input value={search} onChange={event => setSearch(event.target.value)} placeholder="Hae käyttäjää" className="h-12 w-full rounded-xl border border-gray-300 bg-white pl-10 pr-3 text-sm outline-none transition focus:border-blue-500 focus:ring-4 focus:ring-blue-100" />
                </label>
                <div className="mt-3 flex flex-wrap justify-between gap-x-2 gap-y-1 text-xs font-semibold text-gray-500">
                   <span>{visibleUsers.length} {visibleUsers.length === 1 ? 'käyttäjä' : 'käyttäjää'}</span>
                   <span>{users.filter(user => !user.disabled && user.online).length} paikalla</span>
                 </div>
              </div>
              <div className="divide-y divide-gray-100">
                {visibleUsers.map(user => {
                  const active = user._id === focusedUserId
                  return <button key={user._id} type="button" onClick={() => focusUser(user._id)} aria-current={active ? 'true' : undefined} className={`flex min-h-20 w-full items-center gap-3 border-l-4 px-4 py-3 text-left transition ${active ? 'border-blue-600 bg-blue-50' : 'border-transparent hover:bg-gray-50'}`}>
                    <span className={`flex h-10 w-10 flex-none items-center justify-center rounded-full text-sm font-bold ${active ? 'bg-white text-blue-700 shadow-sm' : 'bg-gray-100 text-gray-600'}`}>{initials(user)}</span>
                    <span className="min-w-0 flex-1">
                       <strong className="block truncate text-gray-900">{user.firstName} {user.lastName}</strong>
                       <span className="block truncate text-sm text-gray-500">{user.username}</span>
                       <span className="mt-1 block truncate text-xs text-gray-500">Viimeksi aktiivinen: {formatAccountTime(user.lastActivityAt)}</span>
                     </span>
                    <span className={`shrink-0 rounded-full px-2 py-1 text-[11px] font-bold ${userPresenceStyle(user)}`}>{userPresenceLabel(user)}</span>
                  </button>
                })}
                {visibleUsers.length === 0 && <p className="p-6 text-center text-sm text-gray-500">Hakua vastaavia käyttäjiä ei löytynyt.</p>}
              </div>
            </aside>

            <ActivityErrorBoundary key={`${focusedUserId || 'none'}-${detailTab}`}>
            <article ref={detailRef} className="min-w-0 scroll-mt-20 p-4 sm:p-6 lg:p-8" aria-live="polite">
              {focusedUser ? <>
                <header className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
                  <div className="flex items-center gap-4">
                    <span className="flex h-16 w-16 flex-none items-center justify-center rounded-full bg-blue-50 text-2xl text-blue-600"><FontAwesomeIcon icon={faUser} /></span>
                    <div><div className="flex flex-wrap items-center gap-3"><h2 className="text-2xl font-bold text-gray-900">{focusedUser.firstName} {focusedUser.lastName}</h2><span className={`rounded-full px-2.5 py-1 text-xs font-bold ${userPresenceStyle(focusedUser)}`}>{userPresenceLabel(focusedUser)}</span></div><p className="mt-1 text-gray-500">{focusedUser.username}</p>
                       <p className="mt-1 text-xs text-gray-500">Viimeksi aktiivinen: {formatAccountTime(focusedUser.lastActivityAt)}</p>
                       <p className="mt-0.5 text-xs text-gray-500">Kirjautunut viimeksi: {formatAccountTime(focusedUser.lastLoginAt)}</p>
                     </div>
                  </div>
                </header>

                <div className="mt-5 flex gap-1 overflow-x-auto border-b border-gray-200" role="tablist" aria-label="Käyttäjän tiedot">
                  <button type="button" role="tab" aria-selected={detailTab === 'permissions'} onClick={() => setDetailTab('permissions')} className={`min-h-11 border-b-2 px-4 text-sm font-bold ${detailTab === 'permissions' ? 'border-blue-600 text-blue-700' : 'border-transparent text-gray-500 hover:text-gray-800'}`}>Käyttöoikeudet</button>
                  <button type="button" role="tab" aria-selected={detailTab === 'activity'} onClick={() => setDetailTab('activity')} className={`min-h-11 whitespace-nowrap border-b-2 px-3 text-sm font-bold ${detailTab === 'activity' ? 'border-blue-600 text-blue-700' : 'border-transparent text-gray-500 hover:text-gray-800'}`}>Aktiivisuus</button>
                   <button type="button" role="tab" aria-selected={detailTab === 'logins'} onClick={() => setDetailTab('logins')} className={`min-h-11 whitespace-nowrap border-b-2 px-3 text-sm font-bold ${detailTab === 'logins' ? 'border-blue-600 text-blue-700' : 'border-transparent text-gray-500 hover:text-gray-800'}`}>Kirjautumiset</button>
                </div>

                {detailTab === 'permissions' ? <div role="tabpanel" className="mt-2 divide-y divide-gray-100">
                  {selectedPermissionDefinitions.length > 0 ? selectedPermissionDefinitions.map(permission => <div key={permission.id} className="flex items-start gap-3 py-4"><span className="flex h-10 w-10 flex-none items-center justify-center rounded-xl bg-blue-50 text-blue-600"><FontAwesomeIcon icon={permission.id.startsWith('TASK_') ? faClipboardCheck : faHouse} /></span><div><h3 className="font-bold text-gray-900">{permission.label}</h3><p className="mt-0.5 text-sm text-gray-500">{permission.description}</p></div></div>) : <p className="py-10 text-center text-gray-500">Käyttäjälle ei ole määritetty hallittavia käyttöoikeuksia.</p>}
                </div> : detailTab === 'activity' ? <div role="tabpanel" className="mt-5">
                  {activityLoading ? <div className="flex justify-center py-16 text-blue-600"><FontAwesomeIcon icon={faSpinner} className="mr-2 animate-spin" />Ladataan aktiivisuutta…</div> : activityError ? <div role="alert" className="rounded-xl bg-red-50 p-4 text-sm font-semibold text-red-700">{activityError}</div> : activity ? <>
                    <p className="mb-4 text-sm text-gray-500">Tilastot perustuvat järjestelmään tallennettuun tapahtumahistoriaan viimeisten kuuden kuukauden ajalta.</p>
                    <dl className="grid overflow-hidden rounded-xl bg-gray-50 sm:grid-cols-3 sm:divide-x sm:divide-gray-200">
                      <div className="flex items-center gap-3 border-b border-gray-200 p-4 sm:border-b-0"><FontAwesomeIcon icon={faPen} className="text-xl text-blue-600" /><div><dt className="text-sm text-gray-500">Muokatut kohteet</dt><dd className="text-2xl font-bold text-gray-900">{activity.totals.modifiedAddresses}</dd></div></div>
                      <div className="flex items-center gap-3 border-b border-gray-200 p-4 sm:border-b-0"><FontAwesomeIcon icon={faPlus} className="text-xl text-blue-600" /><div><dt className="text-sm text-gray-500">Luodut kohteet</dt><dd className="text-2xl font-bold text-gray-900">{activity.totals.createdAddresses}</dd></div></div>
                      <div className="flex items-center gap-3 p-4"><FontAwesomeIcon icon={faClipboardCheck} className="text-xl text-blue-600" /><div><dt className="text-sm text-gray-500">Työmerkinnät</dt><dd className="text-2xl font-bold text-gray-900">{activity.totals.taskEntries}</dd></div></div>
                    </dl>
                    <section className="mt-5" aria-labelledby="activity-chart-title"><div className="flex items-center gap-2"><FontAwesomeIcon icon={faChartLine} className="text-blue-600" /><h3 id="activity-chart-title" className="font-bold text-gray-900">Aktiivisuus viimeiset 6 kuukautta</h3></div><div className="mt-3 h-64 rounded-xl border border-gray-200 bg-white p-3">{activityChartData && <MixedChart type="bar" data={activityChartData} options={activityChartOptions} role="img" aria-label={chartDescription} />}</div></section>
                    <section className="mt-5" aria-labelledby="recent-events-title"><h3 id="recent-events-title" className="font-bold text-gray-900">Viimeisimmät tapahtumat</h3>{activity.recentEvents.length > 0 ? <div className="mt-2 divide-y divide-gray-100 rounded-xl border border-gray-200">{activity.recentEvents.map(event => <div key={event.id} className="grid gap-1 px-3 py-3 text-sm sm:grid-cols-[1fr_auto] sm:items-center"><span className="font-semibold text-gray-800">{event.summary}</span><time className="text-gray-500" dateTime={event.createdAt}>{new Date(event.createdAt).toLocaleString('fi-FI', { dateStyle: 'short', timeStyle: 'short' })}</time></div>)}</div> : <p className="mt-2 rounded-xl bg-gray-50 p-4 text-sm text-gray-500">Käyttäjällä ei ole tapahtumia tällä ajanjaksolla.</p>}</section>
                  </> : null}
                </div> : <div role="tabpanel">
                  <LoginHistory key={focusedUser._id} userId={focusedUser._id} sessionToken={globalVariable.sessionToken} />
                </div>}

                <button type="button" onClick={() => manageUser(focusedUser)} className="mt-6 flex min-h-12 w-full items-center justify-center gap-2 rounded-xl border border-blue-200 bg-blue-50 font-bold text-blue-700 transition hover:bg-blue-100 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"><FontAwesomeIcon icon={faPen} />Hallinnoi käyttäjää</button>
              </> : <div className="flex h-full min-h-72 items-center justify-center text-gray-500">Valitse käyttäjä nähdäksesi tiedot.</div>}
            </article>
            </ActivityErrorBoundary>
          </div>
        </section>
      )}
    </main>
  )
}

export default UserAccounts
